import { z } from 'zod';
import type { TFunction } from 'i18next';

// eslint-disable-next-line i18next/no-literal-string -- zod enum members, persisted and switched on by the preview
export const PLATFORMS = ['TikTok', 'Instagram', 'LinkedIn', 'Twitter/X', 'Website'] as const;

export const NAME_MAX_LENGTH = 100;

// eslint-disable-next-line i18next/no-literal-string -- media kind wire values
const ASSET_KINDS = ['image', 'video'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

// eslint-disable-next-line i18next/no-literal-string -- media kind wire values
export const VARIANT_MEDIA_TYPES = ['image', 'video'] as const;
export type VariantMediaType = (typeof VARIANT_MEDIA_TYPES)[number];

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
export const buildSetupFormSchema = (t: TFunction<'validation'>) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, t('contentTest.setupForm.titleIsRequired'))
        .max(NAME_MAX_LENGTH, t('contentTest.setupForm.titleMaxLength', { max: NAME_MAX_LENGTH })),
      platform: z.enum(PLATFORMS, { message: t('contentTest.setupForm.platformIsRequired') }),
      caption: z.string().trim().min(1, t('contentTest.setupForm.captionIsRequired')),
      mediaAssetId: z.string().optional(),
      /** Tracks whether the uploaded/generated mediaAssetId is an image or a video,
       *  so the submit payload can set contentType correctly and the preview can
       *  render the right element. Undefined when no media is set. */
      mediaType: z.enum(ASSET_KINDS).optional(),
      /** Whether the AI-generated variants should be images or videos. Distinct
       *  from `mediaType`, which describes the uploaded input creative. Only
       *  surfaced (and sent) when the `test_content_video_variants` flag is on. */
      variantMediaType: z.enum(VARIANT_MEDIA_TYPES),
      /** Video generation model, required when variantMediaType is 'video'. */
      variantVideoModel: z.string().optional(),
      personaIds: z.array(z.string()).min(1, t('contentTest.setupForm.selectAtLeastOnePersona')),
    })
    .refine((v) => v.variantMediaType !== 'video' || !!v.variantVideoModel, {
      message: t('contentTest.setupForm.selectAVideoModel'),
      path: ['variantVideoModel'],
    });

export type SetupFormValues = z.infer<ReturnType<typeof buildSetupFormSchema>>;

export const SETUP_FORM_DEFAULTS: Partial<SetupFormValues> = {
  name: '',
  caption: '',
  variantMediaType: 'image',
  personaIds: [],
};
